import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { initializeApp } from "firebase/app";
import { getFirestore, doc, setDoc, onSnapshot, collection, addDoc, updateDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, signInWithCustomToken, sendPasswordResetEmail } from "firebase/auth";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PlusCircle, Settings, Edit2, Trash2, LogOut, Download, Sun, Moon, RefreshCw, UserPlus, Filter } from 'lucide-react'; // Added Filter icon

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyDZS6A-POAj7XWPpHrOCEZOj8Emj4a-PBY",
  authDomain: "boardvue-6ead8.firebaseapp.com",
  projectId: "boardvue-6ead8",
  storageBucket: "boardvue-6ead8.appspot.com",
  messagingSenderId: "318624772732",
  appId: "1:318624772732:web:17c7ce971f845ffa938ff9",
  measurementId: "G-99B54YGZ8V"
};

// currentAppId is no longer directly used in Firestore paths, but kept for consistency if needed elsewhere.
// eslint-disable-next-line no-unused-vars
const currentAppId = typeof window !== 'undefined' && window.__app_id ? window.__app_id : firebaseConfig.projectId;
const currentInitialAuthToken = typeof window !== 'undefined' && window.__initial_auth_token ? window.__initial_auth_token : null;


// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// --- Default Data for a New Plan ---
const defaultPlanSettings = {
  committees: ['Board', 'Advancement', 'Building', 'Education', 'Executive', 'Finance', 'Trustees'],
  goals: ['I: Board', 'II: Advancement', 'III: People & Programs', 'IV: Facilities'],
  strategies: ['1', '2', '3', '4', '5', '6', '7', '8'],
  tactics: ['A1', 'A2', 'A3', 'B1', 'B2', 'C1', 'C2'],
  schoolYears: ['23/24', '24/25', '25/26', '26/27'],
  statuses: ['In Progress', 'Completed', 'Blocked', 'On Hold'],
  descriptions: {
    'I: Board': 'The Board of Notre Dame Marist Academy is a strategic entity responsible for safeguarding the Catholic Marist mission...',
    'II: Advancement': 'Focus on fundraising, alumni relations, and community engagement...',
    'III: People & Programs': 'Invest in faculty development, curriculum enhancement, and student support services...',
    'IV: Facilities': 'Maintain and improve campus infrastructure...'
  },
  tacticDescriptions: { // Ensured tacticDescriptions are present
    'A1': 'Develop and implement a comprehensive board member training program.',
    'A2': 'Review and update board governance policies annually.',
    'A3': 'Establish a clear communication protocol between the board and school administration.',
    'B1': 'Launch a new capital campaign for facility improvements.',
    'B2': 'Codify in our Board Profile the importance of training and equipping Board members to understand and support the mission.',
    'C1': 'Implement a new curriculum review process for all academic departments.',
    'C2': 'Expand extracurricular offerings to enhance student development.'
  }
};

// --- Reusable Modal Component for alerts/confirmations ---
function Modal({ show, title, message, onClose, children, onConfirm, confirmText, cancelText }) {
    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-auto transform transition-all scale-100 opacity-100 dark:bg-gray-800 dark:text-gray-200">
                <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{title}</h3>
                {children}
                <div className="mt-6 flex justify-end space-x-3">
                    {onConfirm && (
                        <button
                            onClick={onConfirm}
                            className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition duration-200"
                        >
                            {confirmText || 'Confirm'}
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
                    >
                        {cancelText || 'Close'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// --- AuthContext and AuthProvider ---
const AuthContext = createContext({
    currentUser: null,
    userRole: 'viewer',
    loadingAuth: true,
    signIn: async () => ({ success: false, message: "Auth not ready" }),
    signUp: async () => ({ success: false, message: "Auth not ready" }),
    signOutUser: async () => {},
    updateUserRole: async () => ({ success: false, message: "Auth not ready" }),
    sendPasswordReset: async () => ({ success: false, message: "Auth not ready" }),
    deleteUserAccount: async () => ({ success: false, message: "Auth not ready" })
});

const useAuth = () => {
    return useContext(AuthContext);
};

function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState('viewer');
    const [loadingAuth, setLoadingAuth] = useState(true);

    useEffect(() => {
        let unsubUserDoc;
        console.log("AuthProvider: Setting up auth state listener.");
        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            if (user) {
                console.log("AuthProvider: User detected:", user.email);
                const userDocRef = doc(db, 'users', user.uid);
                unsubUserDoc = onSnapshot(userDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        console.log("AuthProvider: User role fetched:", docSnap.data().role);
                        setCurrentUser({ uid: user.uid, email: user.email, ...docSnap.data() });
                        setUserRole(docSnap.data().role);
                    } else {
                        console.log("AuthProvider: User doc not found, creating with default 'viewer' role.");
                        setDoc(userDocRef, {
                            email: user.email,
                            role: 'viewer',
                            firstName: '',
                            lastName: ''
                        })
                        .then(() => {
                            setCurrentUser({ uid: user.uid, email: user.email, role: 'viewer' });
                            setUserRole('viewer');
                        })
                        .catch(error => console.error("Error setting initial user role:", error));
                    }
                    setLoadingAuth(false);
                }, (error) => {
                    console.error("Error fetching user role snapshot:", error);
                    setLoadingAuth(false);
                });
            } else {
                console.log("AuthProvider: No user detected (logged out or not signed in).");
                setCurrentUser(null);
                setUserRole('viewer');
                setLoadingAuth(false);
                if (unsubUserDoc) unsubUserDoc();
            }
        });

        const signInInitial = async () => {
            console.log("AuthProvider: Attempting initial sign-in...");
            try {
                if (currentInitialAuthToken && currentInitialAuthToken.length > 0) { // Check if token exists and is not empty
                    try {
                        await signInWithCustomToken(auth, currentInitialAuthToken);
                        console.log("AuthProvider: Signed in with custom token.");
                    } catch (customTokenError) {
                        console.error("Custom token sign-in failed:", customTokenError);
                        setCurrentUser(null);
                        setUserRole('viewer');
                    }
                } else {
                    console.log("AuthProvider: No custom token provided or it's empty. User will not be authenticated automatically.");
                    setCurrentUser(null);
                    setUserRole('viewer');
                }
            } catch (error) {
                console.error("Error during initial authentication process:", error);
            } finally {
                setLoadingAuth(false);
            }
        };

        if (loadingAuth) {
            signInInitial();
        }

        return () => {
            console.log("AuthProvider: Cleaning up auth state listener.");
            unsubscribeAuth();
            if (unsubUserDoc) unsubUserDoc();
        };
    }, [loadingAuth]);

    const value = {
        currentUser,
        userRole,
        loadingAuth,
        signIn: async (email, password) => {
            try {
                await signInWithEmailAndPassword(auth, email, password);
                return { success: true };
            } catch (error) {
                return { success: false, message: error.message };
            }
        },
        signUp: async (email, password, firstName = '', lastName = '') => {
            try {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                await setDoc(doc(db, 'users', userCredential.user.uid), {
                    email: userCredential.user.email,
                    role: 'viewer',
                    firstName,
                    lastName
                });
                return { success: true };
            } catch (error) {
                return { success: false, message: error.message };
            }
        },
        signOutUser: async () => {
            await signOut(auth);
        },
        updateUserRole: async (uid, newRole) => {
            try {
                await updateDoc(doc(db, 'users', uid), { role: newRole });
                return { success: true };
            } catch (error) {
                console.error("Error updating user role:", error);
                return { success: false, message: error.message };
            }
        },
        sendPasswordReset: async (email) => {
            try {
                await sendPasswordResetEmail(auth, email);
                return { success: true, message: "Password reset email sent!" };
            } catch (error) {
                return { success: false, message: error.message };
            }
        },
        deleteUserAccount: async (uid) => {
            try {
                // Note: This only deletes the user's document in Firestore.
                // Full Firebase Authentication user deletion requires Admin SDK on a backend.
                await deleteDoc(doc(db, 'users', uid));
                return { success: true, message: "User profile deleted from Firestore." };
            } catch (error) {
                console.error("Error deleting user profile:", error);
                return { success: false, message: error.message };
            }
        }
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// --- DetailItem & UpdateDetail (Used by Dashboard) ---
const DetailItem = ({ label, value }) => (
    <div>
        <p className="font-semibold text-gray-700 dark:text-gray-300">{label}</p>
        <p className="text-gray-600 dark:text-gray-400">{value}</p>
    </div>
);
function UpdateDetail({ update, userRole, onEdit, onDelete }) {
  if (!update) return null;
  const canModify = userRole === 'admin' || (userRole === 'chair');
  return (
    <div className="space-y-4">
        <div className="flex justify-between items-start">
            <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400">{update.goal}</h2>
            {canModify && (<div className="flex space-x-2">
                <button onClick={() => onEdit(update)} className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300">
                    <Edit2 size={20}/>
                </button>
                <button onClick={() => onDelete(update.id)} className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-500">
                    <Trash2 size={20}/>
                </button>
            </div>)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <DetailItem label="Member Name" value={update.memberName} />
            <DetailItem label="Date" value={update.date ? new Date(update.date * 1000).toLocaleDateString() : 'N/A'} />
            <DetailItem label="Committee" value={update.committee} />
            <DetailItem label="Goal" value={update.goal} />
            <DetailItem label="Strategy" value={update.strategy} />
            <DetailItem label="Tactic" value={update.tactic} />
            <DetailItem label="School Year" value={update.schoolYear} />
        </div>
        <div>
            <h4 className="font-semibold text-gray-700 dark:text-gray-300">Update Notes:</h4>
            <p className="mt-1 text-gray-600 bg-gray-50 p-3 rounded-md dark:bg-gray-700 dark:text-gray-400">{update.notes}</p>
        </div>
        {/* Attachment rendering would need to be integrated with Firebase Storage */}
        {update.attachments && update.attachments.length > 0 && (
            <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300">Attachments:</h4>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                    {update.attachments.map((url, index) => (
                        <li key={index}><a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline dark:text-blue-300 dark:hover:text-blue-200 break-all">{url}</a></li>
                    ))}
                </ul>
            </div>
        )}
    </div>
  );
}

// --- Spreadsheet View (Used by Dashboard) ---
function ReportView({ updates }) { // Renamed from ReportView
    const handleExport = () => {
        const headers = ["Date", "MemberName", "Committee", "Goal", "Strategy", "Tactic", "School Year", "Notes"];
        const csv = [
            headers.join(','),
            ...updates.map(row => headers.map(fieldName => {
                const key = fieldName.charAt(0).toLowerCase() + fieldName.slice(1).replace(/\s/g, '');
                let value;
                if (key === 'id') value = row.id;
                else if (key === 'date') value = row.date ? new Date(row.date * 1000).toLocaleDateString() : '';
                else if (key === 'notes') value = (row.notes || '').replace(/"/g, '""');
                else value = row[key];

                return `"${value}"`;
            }).join(','))
        ].join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "boardspective_report.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    return (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow dark:bg-gray-800">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-blue-800 dark:text-blue-400">BoardSpective Updates</h2> {/* Renamed title */}
                <button onClick={handleExport} className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                    <Download size={16}/>
                    <span>Export to CSV</span>
                </button>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Member</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Committee</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Goal</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Strategy</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tactic</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">School Year</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Notes</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                        {updates.map(u => (
                            <tr key={u.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{u.date ? new Date(u.date * 1000).toLocaleDateString() : 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{u.memberName}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{u.committee}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{u.goal}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{u.strategy}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{u.tactic}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{u.schoolYear}</td>
                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                    <p className="w-96 whitespace-pre-wrap">{u.notes}</p>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// --- Sparkline Chart Component ---
const SparklineChart = ({ data, width = 100, height = 20, color = "#4F46E5" }) => {
    if (!data || data.length < 2) {
        return <div className="text-gray-400 text-xs text-center">No data for chart</div>;
    }

    const maxVal = Math.max(...data.map(d => d.count));
    const minVal = Math.min(...data.map(d => d.count));

    // Scale data to SVG coordinates
    const points = data.map((d, i) => {
        const x = (i / (data.length - 1)) * width;
        // Invert Y-axis for SVG (0 is top, height is bottom)
        const y = height - ((d.count - minVal) / (maxVal - minVal || 1)) * height;
        return `${x},${y}`;
    }).join(' ');

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="inline-block align-middle ml-2">
            <polyline
                fill="none"
                stroke={color}
                strokeWidth="2"
                points={points}
            />
        </svg>
    );
};


// --- Executive Report View (Used by Dashboard) ---
function ExecutiveReportView({ updates, settings, summaryContent, onExportPdf, isGeneratingPdf, filters, onFilterChange, onResetFilters }) {
    const [showModal, setShowModal] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [modalMessage, setModalMessage] = useState('');

    return (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow dark:bg-gray-800">
            <div className="flex justify-between items-center mb-6 print:hidden">
                <h2 className="text-xl font-bold text-blue-800 dark:text-blue-400">Executive Summary</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => onExportPdf('executive-summary-content', 'Executive_Summary.pdf')}
                        className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition duration-300"
                        disabled={isGeneratingPdf}
                    >
                        {isGeneratingPdf ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Generating PDF...
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                                <span>Print PDF</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Executive Summary Filters */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow-inner mb-6 print:hidden">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center"><Filter size={18} className="mr-2"/> Filter Summary</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"> {/* Adjusted grid layout */}
                    <div>
                        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date:</label>
                        <input type="date" id="startDate" name="startDate"
                            value={filters.startDate ? filters.startDate.toISOString().slice(0, 10) : ''}
                            onChange={(e) => onFilterChange('startDate', e.target.value ? new Date(e.target.value) : null)}
                            className="p-2 border rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                        />
                    </div>
                    <div>
                        <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">End Date:</label>
                        <input type="date" id="endDate" name="endDate"
                            value={filters.endDate ? filters.endDate.toISOString().slice(0, 10) : ''}
                            onChange={(e) => onFilterChange('endDate', e.target.value ? new Date(e.target.value) : null)}
                            className="p-2 border rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                        />
                    </div>
                    <div className="lg:col-span-1"> {/* This will take 1 column in large screens */}
                        <label htmlFor="filterSearch" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Search:</label>
                        <input type="text" id="filterSearch" name="search"
                            value={filters.search}
                            onChange={(e) => onFilterChange('search', e.target.value)}
                            placeholder="Search notes, members..."
                            className="p-2 border rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                        />
                    </div>
                    {/* Committee, Goal, Strategy, Tactic, School Year in one row on large screens */}
                    <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <div>
                            <label htmlFor="filterCommittee" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Committee:</label>
                            <select id="filterCommittee" name="committee"
                                value={filters.committee}
                                onChange={(e) => onFilterChange('committee', e.target.value)}
                                className="p-2 border rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                            >
                                <option value="">All Committees</option>
                                {settings?.committees?.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="filterGoal" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Goal:</label>
                            <select id="filterGoal" name="goal"
                                value={filters.goal}
                                onChange={(e) => onFilterChange('goal', e.target.value)}
                                className="p-2 border rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                            >
                                <option value="">All Goals</option>
                                {settings?.goals?.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="filterStrategy" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Strategy:</label>
                            <select id="filterStrategy" name="strategy"
                                value={filters.strategy}
                                onChange={(e) => onFilterChange('strategy', e.target.value)}
                                className="p-2 border rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                            >
                                <option value="">All Strategies</option>
                                {settings?.strategies?.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="filterTactic" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tactic:</label>
                            <select id="filterTactic" name="tactic"
                                value={filters.tactic}
                                onChange={(e) => onFilterChange('tactic', e.target.value)}
                                className="p-2 border rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                            >
                                <option value="">All Tactics</option>
                                {settings?.tactics?.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="filterSchoolYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300">School Year:</label>
                            <select id="filterSchoolYear" name="schoolYear"
                                value={filters.schoolYear}
                                onChange={(e) => onFilterChange('schoolYear', e.target.value)}
                                className="p-2 border rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                            >
                                <option value="">All School Years</option>
                                {settings?.schoolYears?.map(sy => <option key={sy} value={sy}>{sy}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="mt-4 text-right">
                    <button onClick={onResetFilters} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 transition duration-300">
                        Reset Filters
                    </button>
                </div>
            </div>


            <div id="executive-summary-content" className="print-area p-4 border border-gray-200 rounded-lg bg-gray-50 min-h-[300px] dark:bg-gray-900 dark:border-gray-700">
                <h1 className="text-3xl font-bold text-center mb-6 print:text-black dark:text-gray-100">Executive Summary</h1>
                {summaryContent.length === 0 ? (
                    <div className="text-center text-gray-500 py-20 dark:text-gray-400">No executive summary available based on current filters.</div>
                ) : (
                    summaryContent.map((item, index) => {
                        if (item.type === 'goalTitle') {
                            return (
                                <h3 key={index} className="text-xl font-bold text-blue-700 mt-6 mb-2 flex items-center dark:text-blue-300">
                                    {item.text}
                                    {item.chartData && <SparklineChart data={item.chartData} />}
                                </h3>
                            );
                        } else if (item.type === 'goalDescription') {
                            return <p key={index} className="text-gray-700 mb-4 dark:text-gray-300">{item.text}</p>;
                        } else if (item.type === 'updateEntry') {
                            return (
                                <div key={index} className="border-l-4 border-blue-200 pl-3 py-2 mb-3 bg-white p-3 rounded-md shadow-sm dark:bg-gray-800 dark:border-blue-700">
                                    <p className="font-semibold text-gray-800 dark:text-gray-200">{item.data.committee} ({item.data.date})</p>
                                    <p className="text-gray-700 dark:text-gray-300">{item.data.notes}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">By: {item.data.memberName} | Strategy: {item.data.strategy} | Tactic: {item.data.tactic}</p>
                                    {item.data.tacticDescription && <p className="text-xs text-gray-500 mt-1 dark:text-gray-500">Tactic Description: {item.data.tacticDescription}</p>}
                                </div>
                            );
                        }
                        return null;
                    })
                )}
                <p className="text-center text-gray-600 mt-6 print:text-gray-800 print:text-sm dark:text-gray-400">
                    Report Generated: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                </p>
            </div>
            <Modal
                show={showModal}
                title="AI Summary Generation"
                message={modalMessage}
                onClose={() => setShowModal(false)}
                cancelText="Close"
            />
        </div>
    );
}

// --- Chart View Component (New) ---
function ChartView({ updates, settings }) {
    const [chartType, setChartType] = useState('goal'); // Default chart type

    // Data for Updates by Goal
    const dataByGoal = useMemo(() => {
        const goalCounts = {};
        settings.goals.forEach(goal => {
            goalCounts[goal] = 0;
        });
        updates.forEach(update => {
            if (update.goal && goalCounts.hasOwnProperty(update.goal)) {
                goalCounts[update.goal]++;
            }
        });
        return Object.keys(goalCounts).map(goal => ({
            name: goal,
            count: goalCounts[goal]
        }));
    }, [updates, settings.goals]);

    // Data for Updates by Committee
    const dataByCommittee = useMemo(() => {
        const committeeCounts = {};
        settings.committees.forEach(committee => {
            committeeCounts[committee] = 0;
        });
        updates.forEach(update => {
            if (update.committee && committeeCounts.hasOwnProperty(update.committee)) {
                committeeCounts[update.committee]++;
            }
        });
        return Object.keys(committeeCounts).map(committee => ({
            name: committee,
            count: committeeCounts[committee]
        }));
    }, [updates, settings.committees]);

    // Data for Updates by Tactic
    const dataByTactic = useMemo(() => {
        const tacticCounts = {};
        settings.tactics.forEach(tactic => {
            tacticCounts[tactic] = 0;
        });
        updates.forEach(update => {
            if (update.tactic && tacticCounts.hasOwnProperty(update.tactic)) {
                tacticCounts[update.tactic]++;
            }
        });
        return Object.keys(tacticCounts).map(tactic => ({
            name: tactic,
            count: tacticCounts[tactic]
        }));
    }, [updates, settings.tactics]);


    const renderChart = () => {
        let data = [];
        let title = "";
        switch (chartType) {
            case 'goal':
                data = dataByGoal;
                title = "Updates by Goal";
                break;
            case 'committee':
                data = dataByCommittee;
                title = "Updates by Committee";
                break;
            case 'tactic':
                data = dataByTactic;
                title = "Updates by Tactic";
                break;
            default:
                data = dataByGoal;
                title = "Updates by Goal";
        }

        if (data.length === 0) {
            return <div className="text-center text-gray-500 py-20 dark:text-gray-400">No updates available to display in chart for {title.toLowerCase()}.</div>;
        }

        return (
            <ResponsiveContainer width="100%" height={400}>
                <BarChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} stroke="#6B7280" /> {/* Dark mode stroke */}
                    <YAxis stroke="#6B7280" /> {/* Dark mode stroke */}
                    <Tooltip contentStyle={{ backgroundColor: 'rgb(31 41 55)', borderColor: 'rgb(75 85 99)', color: 'rgb(229 231 235)' }} /> {/* Dark mode tooltip */}
                    <Legend />
                    <Bar dataKey="count" fill="#8884d8" name="Updates" />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    return (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow dark:bg-gray-800">
            <h2 className="text-xl font-bold text-blue-800 dark:text-blue-400 mb-4">Charts</h2>
            <div className="flex justify-center mb-4 space-x-2">
                <button onClick={() => setChartType('goal')} className={`px-4 py-2 rounded-md ${chartType === 'goal' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>By Goal</button>
                <button onClick={() => setChartType('committee')} className={`px-4 py-2 rounded-md ${chartType === 'committee' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>By Committee</button>
                <button onClick={() => setChartType('tactic')} className={`px-4 py-2 rounded-md ${chartType === 'tactic' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>By Tactic</button>
            </div>
            {renderChart()}
        </div>
    );
}

// --- Goals and Tactics View Component ---
function GoalTacticView({ settings }) {
  const goals = settings.goals || [];
  const tactics = settings.tactics || [];
  const goalDescriptions = settings.descriptions || {};
  const tacticDescriptions = settings.tacticDescriptions || {};

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow dark:bg-gray-800">
      <h2 className="text-xl font-bold text-blue-800 dark:text-blue-400 mb-6">Goals and Tactics</h2>
      <div className="space-y-8">
        {goals.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">
            No goals defined yet. Please add them in Settings.
          </p>
        ) : (
          goals.map((goal) => (
            <div
              key={goal}
              className="border-b pb-4 mb-4 last:border-b-0 dark:border-gray-700"
            >
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                {goal}
              </h3>
              <p className="text-gray-600 text-sm mb-4 dark:text-gray-300">
                {goalDescriptions[goal] ||
                  "No description available for this goal."}
              </p>
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-2">
                Associated Tactics:
              </h4>
              {tactics.filter(() => true).length === 0 ? (
                <p className="text-gray-500 text-sm pl-4 dark:text-gray-400">
                  No tactics associated or defined.
                </p>
              ) : (
                <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 pl-4">
                  {tactics.map((tactic) => (
                    <React.Fragment key={tactic}>
                      <div className="font-medium text-gray-700 dark:text-gray-300">
                        {tactic}:
                      </div>
                      <div className="text-gray-700 dark:text-gray-300">
                        {tacticDescriptions[tactic] || "No description available."}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// --- Update Entry Form (Used by MainApp) ---
function UpdateEntryForm({ onSave, onCancel, settings, updateToEdit, users }) {
  const { currentUser, userRole } = useAuth();
  const isAdmin = userRole === 'admin';
  const isChair = userRole === 'chair';
  const currentUserFullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();

  const [formData, setFormData] = useState({
    id: updateToEdit?.id || null,
    date: updateToEdit?.date ? new Date(updateToEdit.date * 1000) : new Date(),
    memberName: updateToEdit?.memberName || (isChair ? currentUserFullName : ''),
    committee: updateToEdit?.committee || '',
    goal: updateToEdit?.goal || '',
    strategy: updateToEdit?.strategy || '',
    tactic: updateToEdit?.tactic || '',
    schoolYear: updateToEdit?.schoolYear || '',
    notes: updateToEdit?.notes || '',
    status: updateToEdit?.status || '',
    attachments: updateToEdit?.attachments || [],
  });

  const formattedDate = formData.date ? formData.date.toISOString().slice(0, 10) : '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "date") {
      setFormData(prev => ({ ...prev, [name]: new Date(value) }));
    } else if (name === "memberName" && isAdmin) {
      const selectedUser = users.find(user => `${user.firstName} ${user.lastName}` === value);
      setFormData(prev => ({ ...prev, memberName: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-lg dark:bg-gray-800">
      <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-6">{updateToEdit ? 'Edit Update' : 'New Update Entry'}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
            <input type="date" id="date" name="date" value={formattedDate} onChange={handleChange} className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
          </div>
          <div>
            <label htmlFor="memberName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Member Name <span className="text-red-500">*</span></label>
            {isAdmin ? (
              <select id="memberName" name="memberName" value={formData.memberName} onChange={handleChange} className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required>
                <option value="">Select Member</option>
                {users.map(user => (
                  <option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                id="memberName"
                name="memberName"
                value={formData.memberName}
                readOnly
                className="p-3 border border-gray-300 rounded-md w-full bg-gray-100 cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            )}
          </div>
          <div>
            <label htmlFor="schoolYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">School Year <span className="text-red-500">*</span></label>
            <select id="schoolYear" name="schoolYear" value={formData.schoolYear} onChange={handleChange} className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required>
              <option value="">Select Year</option>
              {settings?.schoolYears?.map(sy => <option key={sy} value={sy}>{sy}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="committee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Committee <span className="text-red-500">*</span></label>
            <select id="committee" name="committee" value={formData.committee} onChange={handleChange} className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required>
              <option value="">Select Committee</option>
              {settings?.committees?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="goal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Goal <span className="text-red-500">*</span></label>
            <select id="goal" name="goal" value={formData.goal} onChange={handleChange} className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required>
              <option value="">Select Goal</option>
              {settings?.goals?.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="strategy" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
            <select id="strategy" name="strategy" value={formData.strategy} onChange={handleChange} className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
              <option value="">Select Strategy</option>
              {settings?.strategies?.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="tactic" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tactic</label>
            <select id="tactic" name="tactic" value={formData.tactic} onChange={handleChange} className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
              <option value="">Select Tactic</option>
              {settings?.tactics?.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status <span className="text-red-500">*</span></label>
            <select id="status" name="status" value={formData.status} onChange={handleChange} className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required>
              <option value="">Select Status</option>
              {settings?.statuses?.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Update Notes</label>
          <textarea id="notes" name="notes" rows="4" value={formData.notes} onChange={handleChange} placeholder="Provide detailed updates here..." className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"></textarea>
        </div>

        <div>
          <label htmlFor="attachments" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attachment URLs</label>
          {formData.attachments.map((url, index) => (
            <div key={index} className="flex items-center space-x-2 mb-2">
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  const newAttachments = [...formData.attachments];
                  newAttachments[index] = e.target.value;
                  setFormData(prev => ({ ...prev, attachments: newAttachments }));
                }}
                placeholder="Enter attachment URL"
                className="p-3 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
              />
              <button
                type="button"
                onClick={() => {
                  const newAttachments = formData.attachments.filter((_, i) => i !== index);
                  setFormData(prev => ({ ...prev, attachments: newAttachments }));
                }}
                className="p-2 text-red-500 hover:bg-red-100 rounded-full dark:hover:bg-red-900"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ''] }))}
            className="mt-2 text-sm text-blue-600 hover:underline flex items-center dark:text-blue-300 dark:hover:text-blue-200"
          >
            <PlusCircle size={16} className="mr-1" /> Add Attachment URL
          </button>
        </div>

        <div className="flex justify-end space-x-4 mt-6">
          <button type="button" onClick={onCancel} className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition duration-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Cancel</button>
          <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300">Save Update</button>
        </div>
      </form>
    </div>
  );
}

// --- Settings Page (Used by MainApp) ---
function SettingsPage({ settings, users, onSave, onCancel, onCreatePlan }) {
  const [localSettings, setLocalSettings] = useState(JSON.parse(JSON.stringify(settings)));
  const [localUsers, setLocalUsers] = useState(JSON.parse(JSON.stringify(users)));
  const [newPlanName, setNewPlanName] = useState("");
  const [newTacticDescriptionTactic, setNewTacticDescriptionTactic] = useState('');
  const [newTacticDescriptionText, setNewTacticDescriptionText] = useState('');

  // New states for user creation
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');

  // New states for goal descriptions
  const [newGoalDescriptionGoal, setNewGoalDescriptionGoal] = useState('');
  const [newGoalDescriptionText, setNewGoalDescriptionText] = useState('');


  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmModalAction, setConfirmModalAction] = useState(null);
  const [modalConfirmText, setModalConfirmText] = useState(''); // New state for modal confirm button text
  const [modalCancelText, setModalCancelText] = useState('Close'); // New state for modal cancel button text


  const { signUp, sendPasswordReset } = useAuth(); // Removed deleteUserAccount as it's no longer used

  useEffect(() => { setLocalSettings(JSON.parse(JSON.stringify(settings)))}, [settings]);
  useEffect(() => { setLocalUsers(JSON.parse(JSON.stringify(users)))}, [users]);

  // Helper function to update a specific field in localSettings
  const updateLocalSettingField = (fieldName, value) => {
      setLocalSettings(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleAddItem = (listName, newItem, setNewItemState) => {
    if (newItem.trim() === '') return;
    if (localSettings[listName].includes(newItem.trim())) {
      setConfirmModalMessage(`${newItem.trim()} already exists in ${listName}.`);
      setConfirmModalAction(null);
      setModalConfirmText(null); // No confirm button for informational message
      setModalCancelText("Close");
      setShowConfirmModal(true);
      return;
    }
    setLocalSettings(prev => ({
      ...prev,
      [listName]: [...prev[listName], newItem.trim()]
    }));
    setNewItemState('');
  };

  const handleRemoveItem = (listName, itemToRemove) => {
    setConfirmModalMessage(`Are you sure you want to remove "${itemToRemove}" from ${listName}? This cannot be undone.`);
    setModalConfirmText("Yes, Remove");
    setModalCancelText("Cancel");
    setConfirmModalAction(() => () => {
        setLocalSettings(prev => ({
            ...prev,
            [listName]: prev[listName].filter(item => item !== itemToRemove)
        }));
        setShowConfirmModal(false);
        setConfirmModalAction(null);
    });
    setShowConfirmModal(true);
  };

  const handleAddTacticDescription = () => {
    if (newTacticDescriptionTactic.trim() === '' || newTacticDescriptionText.trim() === '') {
      setConfirmModalMessage('Tactic and description cannot be empty.');
      setConfirmModalAction(null);
      setModalConfirmText(null);
      setModalCancelText("Close");
      setShowConfirmModal(true);
      return;
    }
    setLocalSettings(prev => ({
      ...prev,
      tacticDescriptions: {
        ...prev.tacticDescriptions,
        [newTacticDescriptionTactic.trim()]: newTacticDescriptionText.trim()
      }
    }));
    setNewTacticDescriptionTactic('');
    setNewTacticDescriptionText('');
  };

  const handleRemoveTacticDescription = (tactic) => {
    setConfirmModalMessage(`Are you sure you want to remove the description for tactic "${tactic}"?`);
    setModalConfirmText("Yes, Remove");
    setModalCancelText("Cancel");
    setConfirmModalAction(() => () => {
        setLocalSettings(prev => {
            const newTacticDescriptions = { ...prev.tacticDescriptions };
            delete newTacticDescriptions[tactic];
            return {
                ...prev,
                tacticDescriptions: newTacticDescriptions
            };
        });
        setShowConfirmModal(false);
        setConfirmModalAction(null);
    });
    setShowConfirmModal(true);
  };

  // New functions for Goal Descriptions
  const handleAddGoalDescription = () => {
    if (newGoalDescriptionGoal.trim() === '' || newGoalDescriptionText.trim() === '') {
      setConfirmModalMessage('Goal and description cannot be empty.');
      setConfirmModalAction(null);
      setModalConfirmText(null);
      setModalCancelText("Close");
      setShowConfirmModal(true);
      return;
    }
    setLocalSettings(prev => ({
      ...prev,
      descriptions: { // This is the 'descriptions' object for goals
        ...prev.descriptions,
        [newGoalDescriptionGoal.trim()]: newGoalDescriptionText.trim()
      }
    }));
    setNewGoalDescriptionGoal('');
    setNewGoalDescriptionText('');
  };

  const handleRemoveGoalDescription = (goal) => {
    setConfirmModalMessage(`Are you sure you want to remove the description for goal "${goal}"?`);
    setModalConfirmText("Yes, Remove");
    setModalCancelText("Cancel");
    setConfirmModalAction(() => () => {
        setLocalSettings(prev => {
            const newGoalDescriptions = { ...prev.descriptions };
            delete newGoalDescriptions[goal];
            return {
                ...prev,
                descriptions: newGoalDescriptions
            };
        });
        setShowConfirmModal(false);
        setConfirmModalAction(null);
    });
    setShowConfirmModal(true);
  };


  const handleUserRoleChange = (uid, newRole) => {
    setLocalUsers(localUsers.map(u => u.id === uid ? { ...u, role: newRole } : u));
  };

  const handleUserFieldChange = (uid, field, value) => {
    setLocalUsers(localUsers.map(u => u.id === uid ? { ...u, [field]: value } : u));
  };

  // New function to create a new user
  const handleCreateUser = async () => {
      if (!newUserEmail || !newUserPassword || !newUserRole) {
          setConfirmModalMessage("Email, password, and role are required to create a user.");
          setConfirmModalAction(null);
          setModalConfirmText(null);
          setModalCancelText("Close");
          setShowConfirmModal(true);
          return;
      }
      try {
          const result = await signUp(newUserEmail, newUserPassword, newUserFirstName, newUserLastName);
          if (result.success) {
              setConfirmModalMessage(
                `User "${newUserEmail}" created successfully. You have been automatically logged in as this new user. ` +
                `Please log out and sign back in with your admin account to continue managing settings.`
              );
              setConfirmModalAction(null); // No confirm action needed, just close
              setModalConfirmText(null);
              setModalCancelText("Close");
              setShowConfirmModal(true);
              setNewUserEmail('');
              setNewUserPassword('');
              setNewUserFirstName('');
              setNewUserLastName('');
              setNewUserRole('viewer'); // Reset to default
          } else {
              setConfirmModalMessage(`Failed to create user: ${result.message}`);
              setConfirmModalAction(null);
              setModalConfirmText(null);
              setModalCancelText("Close");
              setShowConfirmModal(true);
          }
      } catch (error) {
          console.error("Error creating user:", error);
          setConfirmModalMessage(`Failed to create user: ${error.message}`);
          setConfirmModalAction(null);
          setModalConfirmText(null);
          setModalCancelText("Close");
          setShowConfirmModal(true);
      }
  };

  // Function to handle password reset for a specific user
  const handleUserPasswordReset = async (email) => {
      setConfirmModalMessage(`Are you sure you want to send a password reset email to "${email}"?`);
      setModalConfirmText("Send Reset Email");
      setModalCancelText("Cancel");
      setConfirmModalAction(() => async () => {
          try {
              const result = await sendPasswordReset(email);
              if (result.success) {
                  setConfirmModalMessage(`Password reset email sent to ${email}.`);
              } else {
                  setConfirmModalMessage(`Failed to send password reset email to ${email}: ${result.message}`);
              }
          } catch (error) {
              console.error("Error initiating password reset:", error);
              setConfirmModalMessage(`An unexpected error occurred during password reset: ${error.message}`);
          } finally {
              setConfirmModalAction(null); // Clear action after execution
              setModalConfirmText(null); // No confirm button for final message
              setModalCancelText("Close");
              setShowConfirmModal(true);
          }
      });
      setShowConfirmModal(true);
  };


  const EditableList = ({ title, list, newItem, setNewItem, onAddItem, onRemoveItem }) => (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 mb-4 dark:bg-gray-800 dark:border-gray-700">
      <h3 className="text-xl font-semibold text-gray-800 mb-3 dark:text-gray-200">{title}</h3>
      <div className="flex items-center space-x-2 mb-4">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder={`Add new ${title.toLowerCase().slice(6, -1)}`}
          className="flex-grow p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
        />
        <button onClick={onAddItem} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add</button>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto border p-2 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600">
        {list.length === 0 ? (
            <p className="text-gray-500 text-sm dark:text-gray-400">No {title.toLowerCase().slice(6)} added yet.</p>
        ) : (
            list.map((item, index) => (
                <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded-md shadow-sm dark:bg-gray-600">
                    <span className="text-gray-700 dark:text-gray-200">{item}</span>
                    <button onClick={() => onRemoveItem(item)} className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-500">
                        <Trash2 size={16}/>
                    </button>
                </div>
            ))
        )}
      </div>
    </div>
  );

  const handleSaveClick = () => {
    onSave(localSettings, localUsers);
  };

  return (
    <div className="max-w-6xl mx-auto bg-white p-8 rounded-xl shadow-lg space-y-8 dark:bg-gray-800">
        {/* Strategic Plan Management */}
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4 border-b pb-2 dark:border-gray-700">Strategic Plan Management</h2>
            <div className="flex items-center space-x-2">
                 <input type="text" value={newPlanName} onChange={(e) => setNewPlanName(e.target.value)} placeholder="New Strategic Plan Name..." className="flex-grow p-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"/>
                 <button onClick={() => { onCreatePlan(newPlanName); setNewPlanName(''); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Create Plan</button>
            </div>
        </div>

        {/* Create New User Account Section */}
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4 border-b pb-2 dark:border-gray-700">Create New User Account</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="User Email"
                    className="p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                />
                <input
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="User Password"
                    className="p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                />
                <input
                    type="text"
                    value={newUserFirstName}
                    onChange={(e) => setNewUserFirstName(e.target.value)}
                    placeholder="First Name"
                    className="p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                />
                <input
                    type="text"
                    value={newUserLastName}
                    onChange={(e) => setNewUserLastName(e.target.value)}
                    placeholder="Last Name"
                    className="p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                />
            </div>
            <div className="flex items-center space-x-4">
                <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                    className="p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                >
                    <option value="viewer">Viewer</option>
                    <option value="chair">Chair</option>
                    <option value="admin">Admin</option>
                    <option value="disabled">Disabled</option>
                </select>
                <button onClick={handleCreateUser} className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2">
                    <UserPlus size={20}/> Create User
                </button>
            </div>
        </div>

        {/* User Role Management */}
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4 border-b pb-2 dark:border-gray-700">User Role Management</h2>
            <div className="space-y-3">
                {localUsers.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400">No users found.</p>
                ) : (
                    localUsers.map(user => (
                        <div key={user.id} className="flex flex-col sm:flex-row justify-between items-center bg-white p-3 rounded-lg shadow-sm gap-2 dark:bg-gray-700">
                            <div className="flex-grow grid grid-cols-1 md:grid-cols-3 gap-2 items-center w-full sm:w-auto">
                                <p className="font-medium text-gray-800 break-all dark:text-gray-200">{user.email}</p>
                                <input
                                    type="text"
                                    value={user.firstName || ''}
                                    onChange={(e) => handleUserFieldChange(user.id, 'firstName', e.target.value)}
                                    placeholder="First Name"
                                    className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                                />
                                <input
                                    type="text"
                                    value={user.lastName || ''}
                                    onChange={(e) => handleUserFieldChange(user.id, 'lastName', e.target.value)}
                                    placeholder="Last Name"
                                    className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                                />
                            </div>
                            <div className="flex items-center space-x-2 mt-2 sm:mt-0 flex-shrink-0">
                                <label className="text-sm text-gray-600 dark:text-gray-300">Role:</label>
                                <select 
                                    value={user.role} 
                                    onChange={(e) => handleUserRoleChange(user.id, e.target.value)} 
                                    className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 transition duration-300 font-medium text-sm shadow-sm"
                                >
                                    <option value="viewer">Viewer</option>
                                    <option value="chair">Chair</option>
                                    <option value="admin">Admin</option>
                                    <option value="disabled">Disabled</option>
                                </select>
                                <button
                                    onClick={() => handleUserPasswordReset(user.email)} // Call the new handler
                                    className="px-3 py-1.5 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition duration-300 font-semibold text-sm shadow-md"
                                    title="Send Password Reset Email"
                                >
                                    Reset Password
                                </button>
                                {/* Removed Delete User Button */}
                            </div>
                        </div>
                    ))
                )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">Roles define user permissions within the application.</p>
        </div>

        {/* Strategic Objectives & Categories */}
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4 border-b pb-2 dark:border-gray-700">Strategic Objectives & Categories</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EditableList 
                    title="Manage Goals" 
                    list={localSettings.goals} 
                    newItem={localSettings.newGoal || ''} 
                    setNewItem={(value) => updateLocalSettingField('newGoal', value)} 
                    onAddItem={() => handleAddItem('goals', localSettings.newGoal, (val) => updateLocalSettingField('newGoal', val))} 
                    onRemoveItem={(item) => handleRemoveItem('goals', item)} 
                />
                <EditableList 
                    title="Manage Committees" 
                    list={localSettings.committees} 
                    newItem={localSettings.newCommittee || ''} 
                    setNewItem={(value) => updateLocalSettingField('newCommittee', value)} 
                    onAddItem={() => handleAddItem('committees', localSettings.newCommittee, (val) => updateLocalSettingField('newCommittee', val))} 
                    onRemoveItem={(item) => handleRemoveItem('committees', item)} 
                />
                <EditableList 
                    title="Manage Strategies" 
                    list={localSettings.strategies} 
                    newItem={localSettings.newStrategy || ''} 
                    setNewItem={(value) => updateLocalSettingField('newStrategy', value)} 
                    onAddItem={() => handleAddItem('strategies', localSettings.newStrategy, (val) => updateLocalSettingField('newStrategy', val))} 
                    onRemoveItem={(item) => handleRemoveItem('strategies', item)} 
                />
                <EditableList 
                    title="Manage Tactics" 
                    list={localSettings.tactics} 
                    newItem={localSettings.newTactic || ''} 
                    setNewItem={(value) => updateLocalSettingField('newTactic', value)} 
                    onAddItem={() => handleAddItem('tactics', localSettings.newTactic, (val) => updateLocalSettingField('newTactic', val))} 
                    onRemoveItem={(item) => handleRemoveItem('tactics', item)} 
                />
                <EditableList 
                    title="Manage School Years" 
                    list={localSettings.schoolYears} 
                    newItem={localSettings.newSchoolYear || ''} 
                    setNewItem={(value) => updateLocalSettingField('newSchoolYear', value)} 
                    onAddItem={() => handleAddItem('schoolYears', localSettings.newSchoolYear, (val) => updateLocalSettingField('newSchoolYear', val))} 
                    onRemoveItem={(item) => handleRemoveItem('schoolYears', item)} 
                />
                <EditableList 
                    title="Manage Statuses" 
                    list={localSettings.statuses} 
                    newItem={localSettings.newStatus || ''} 
                    setNewItem={(value) => updateLocalSettingField('newStatus', value)} 
                    onAddItem={() => handleAddItem('statuses', localSettings.newStatus, (val) => updateLocalSettingField('newStatus', val))} 
                    onRemoveItem={(item) => handleRemoveItem('statuses', item)} 
                />
            </div>
        </div>

        {/* Goal Descriptions Management */}
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4 border-b pb-2 dark:border-gray-700">Manage Goal Descriptions</h2>
            <div className="flex items-center space-x-2 mb-4">
                <select
                    value={newGoalDescriptionGoal}
                    onChange={(e) => setNewGoalDescriptionGoal(e.target.value)}
                    className="p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                >
                    <option value="">Select Goal</option>
                    {localSettings.goals.map(goal => (
                        <option key={goal} value={goal}>{goal}</option>
                    ))}
                </select>
                <input
                    type="text"
                    value={newGoalDescriptionText}
                    onChange={(e) => setNewGoalDescriptionText(e.target.value)}
                    placeholder="Enter description for selected goal"
                    className="flex-grow p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                />
                <button onClick={handleAddGoalDescription} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add/Update</button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto border p-2 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600">
                {Object.keys(localSettings.descriptions || {}).length === 0 ? (
                    <p className="text-gray-500 text-sm dark:text-gray-400">No goal descriptions added yet.</p>
                ) : (
                    Object.entries(localSettings.descriptions || {}).map(([goal, description]) => (
                        <div key={goal} className="flex justify-between items-center bg-gray-50 p-2 rounded-md shadow-sm dark:bg-gray-600">
                            <span className="text-gray-700 font-semibold dark:text-gray-200">{goal}:</span>
                            <span className="text-gray-600 flex-grow ml-2 dark:text-gray-300">{description}</span>
                            <button onClick={() => handleRemoveGoalDescription(goal)} className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-500">
                                <Trash2 size={16}/>
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>

        {/* Tactic Descriptions Management */}
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4 border-b pb-2 dark:border-gray-700">Manage Tactic Descriptions</h2>
            <div className="flex items-center space-x-2 mb-4">
                <select
                    value={newTacticDescriptionTactic}
                    onChange={(e) => setNewTacticDescriptionTactic(e.target.value)}
                    className="p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                >
                    <option value="">Select Tactic</option>
                    {localSettings.tactics.map(tactic => (
                        <option key={tactic} value={tactic}>{tactic}</option>
                    ))}
                </select>
                <input
                    type="text"
                    value={newTacticDescriptionText}
                    onChange={(e) => setNewTacticDescriptionText(e.target.value)}
                    placeholder="Enter description for selected tactic"
                    className="flex-grow p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                />
                <button onClick={handleAddTacticDescription} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add/Update</button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto border p-2 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600">
                {Object.keys(localSettings.tacticDescriptions || {}).length === 0 ? (
                    <p className="text-gray-500 text-sm dark:text-gray-400">No tactic descriptions added yet.</p>
                ) : (
                    Object.entries(localSettings.tacticDescriptions || {}).map(([tactic, description]) => (
                        <div key={tactic} className="flex justify-between items-center bg-gray-50 p-2 rounded-md shadow-sm dark:bg-gray-600">
                            <span className="text-gray-700 font-semibold dark:text-gray-200">{tactic}:</span>
                            <span className="text-gray-600 flex-grow ml-2 dark:text-gray-300">{description}</span>
                            <button onClick={() => handleRemoveTacticDescription(tactic)} className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-500">
                                <Trash2 size={16}/>
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>


        <div className="flex justify-end space-x-4 mt-8 border-t pt-6 border-gray-200 dark:border-gray-700">
            <button type="button" onClick={onCancel} 
                    className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-300 font-medium text-lg transform hover:scale-105">
                Cancel
            </button>
            <button type="button" onClick={handleSaveClick} 
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition duration-300 font-semibold shadow-md text-lg transform hover:scale-105">
                Save All Settings
            </button>
        </div>
        <Modal
            show={showConfirmModal}
            title="Confirm Action"
            message={confirmModalMessage}
            onClose={() => {
                setShowConfirmModal(false);
                setConfirmModalAction(null); // Clear action when modal closes
            }}
            onConfirm={confirmModalAction} // Pass the action directly
            confirmText={modalConfirmText}
            cancelText={modalCancelText}
        />
    </div>
  );
}

// --- Dashboard Component (Used by MainApp) ---
function Dashboard({ updates, onEdit, onDelete, settings, userRole, onAddNew, activePlanId, executiveSummaryContent, handleExportPdf, isGeneratingPdf, executiveSummaryFilters, onExecutiveSummaryFilterChange, onResetExecutiveSummaryFilters }) {
  const [filters, setFilters] = useState({ search: '', committee: '', goal: '', strategy: '', tactic: '', schoolYear: '', status: '' });
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [viewMode, setViewMode] = useState('goalTactic'); // Default to 'goalTactic'

  const filteredUpdates = useMemo(() => {
    let sortedUpdates = [...updates].sort((a,b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
    return sortedUpdates.filter(u => {
      const searchStr = `${u.memberName} ${u.notes} ${u.committee} ${u.goal} ${u.strategy} ${u.tactic} ${u.schoolYear} ${u.status}`.toLowerCase();
      return (
        (filters.search === '' || searchStr.includes(filters.search.toLowerCase())) &&
        (filters.committee === '' || u.committee === filters.committee) &&
        (filters.goal === '' || u.goal === filters.goal) &&
        (filters.strategy === '' || u.strategy === filters.strategy) &&
        (filters.tactic === '' || u.tactic === filters.tactic) &&
        (filters.schoolYear === '' || u.schoolYear === filters.schoolYear) &&
        (filters.status === '' || u.status === filters.status)
      );
    });
  }, [updates, filters]);
  
  useEffect(() => {
    if (filteredUpdates.length > 0) {
      if (!selectedUpdate || !filteredUpdates.find(u => u.id === selectedUpdate.id)) {
        setSelectedUpdate(filteredUpdates[0]);
      }
    } else if (filteredUpdates.length === 0) {
      setSelectedUpdate(null);
    }
  }, [filteredUpdates, selectedUpdate]);

  const handleFilterChange = (e) => { const { name, value } = e.target; setFilters(prev => ({ ...prev, [name]: value })); };
  
  const handleResetFilters = () => {
      setFilters({ search: '', committee: '', goal: '', strategy: '', tactic: '', schoolYear: '', status: '' });
  };

  // Display message if no active plan
  if (!activePlanId) {
      return (
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] bg-gray-50 dark:bg-gray-900 p-6 rounded-lg shadow-inner">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 dark:text-gray-500 mb-4"><path d="M14.5 2H6a2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><line x1="10" y1="9" x2="8" y2="9"></line></svg>
              <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2">No Strategic Plan Selected or Created</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 text-center mb-4">
                  Please go to the <span className="font-semibold">Settings</span> page to create a new strategic plan or select an existing one.
              </p>
              {(userRole === 'admin' || userRole === 'chair') && (
                  <button
                      onClick={() => onAddNew()}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 flex items-center space-x-2"
                  >
                      <PlusCircle size={20}/>
                      <span>New Update</span>
                  </button>
              )}
          </div>
      );
  }


  return (
    <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow flex flex-wrap gap-4 items-center justify-between print:hidden">
            <div className="flex flex-wrap gap-4 items-center">
                 <input type="text" name="search" placeholder="Search..." value={filters.search} onChange={handleFilterChange} className="p-2 border rounded-md w-full sm:w-auto bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400" />
                <select name="committee" value={filters.committee} onChange={handleFilterChange} className="p-2 border rounded-md w-full sm:w-auto bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"><option value="">All Committees</option>{settings?.committees?.map(c => <option key={c} value={c}>{c}</option>)}</select>
                <select name="goal" value={filters.goal} onChange={handleFilterChange} className="p-2 border rounded-md w-full sm:w-auto bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"><option value="">All Goals</option>{settings?.goals?.map(g => <option key={g} value={g}>{g}</option>)}</select>
                <select name="strategy" value={filters.strategy} onChange={handleFilterChange} className="p-2 border rounded-md w-full sm:w-auto bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"><option value="">All Strategies</option>{settings?.strategies?.map(s => <option key={s} value={s}>{s}</option>)}</select>
                <select name="tactic" value={filters.tactic} onChange={handleFilterChange} className="p-2 border rounded-md w-full sm:w-auto bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"><option value="">All Tactics</option>{settings?.tactics?.map(t => <option key={t} value={t}>{t}</option>)}</select>
                <select name="schoolYear" value={filters.schoolYear} onChange={handleFilterChange} className="p-2 border rounded-md w-full sm:w-auto bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"><option value="">All School Years</option>{settings?.schoolYears?.map(sy => <option key={sy} value={sy}>{sy}</option>)}</select>
                <select name="status" value={filters.status} onChange={handleFilterChange} className="p-2 border rounded-md w-full sm:w-auto bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"><option value="">All Statuses</option>{settings?.statuses?.map(s => <option key={s} value={s}>{s}</option>)}</select>
            </div>
            <div className="flex space-x-2"> {/* Container for Reset and New Update buttons */}
                <button onClick={handleResetFilters} className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition duration-300 w-full sm:w-auto dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                    <RefreshCw size={20}/>
                    <span>Reset Filters</span>
                </button>
                { (userRole === 'admin' || userRole === 'chair') &&
                    <button onClick={onAddNew} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300 w-full sm:w-auto">
                        <PlusCircle size={20}/>
                        <span>New Update</span>
                    </button>
                }
            </div>
        </div>
        <div className="flex justify-center mb-4 border-b-2 border-gray-200 dark:border-gray-700 print:hidden">
            <button onClick={() => setViewMode('goalTactic')} className={`px-4 py-2 text-base font-medium ${viewMode === 'goalTactic' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 dark:text-gray-400'}`}>Goals and Tactics</button> {/* Renamed tab */}
            <button onClick={() => setViewMode('detail')} className={`px-4 py-2 text-base font-medium ${viewMode === 'detail' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 dark:text-gray-400'}`}>Detailed View</button>
            <button onClick={() => setViewMode('report')} className={`px-4 py-2 text-base font-medium ${viewMode === 'report' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 dark:text-gray-400'}`}>Spreadsheet View</button> {/* Renamed tab */}
            <button onClick={() => setViewMode('executive')} className={`px-4 py-2 text-base font-medium ${viewMode === 'executive' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 dark:text-gray-400'}`}>Executive Summary</button>
            <button onClick={() => setViewMode('chart')} className={`px-4 py-2 text-base font-medium ${viewMode === 'chart' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 dark:text-gray-400'}`}>Progress Charts</button>
        </div>
        
        {viewMode === 'detail' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1 bg-white dark:bg-gray-800 rounded-lg shadow">
                    <div className="h-[600px] overflow-y-auto px-2 py-2">
                        {filteredUpdates.length === 0 ? (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-20">No updates found for the current filters.</div>
                        ) : (
                            filteredUpdates.map(update => (
                                <div 
                                    key={update.id} 
                                    onClick={() => setSelectedUpdate(update)} 
                                    className={`pl-4 py-2 my-1 border-l-4 cursor-pointer rounded-r-md 
                                        ${selectedUpdate?.id === update.id ? 'bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-400' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'}
                                        transition-colors duration-200`}
                                >
                                    <p className="font-bold text-gray-800 dark:text-gray-200">{update.committee}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">{update.date ? new Date(update.date * 1000).toLocaleDateString() : 'N/A'}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{update.memberName}</p> {/* Now displays full name */}
                                </div>
                            ))
                        )}
                    </div>
                </div>
                <div className="md:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-lg shadow">{selectedUpdate ? <UpdateDetail update={selectedUpdate} userRole={userRole} onEdit={onEdit} onDelete={onDelete} /> : <div className="text-center text-gray-500 dark:text-gray-400 py-20">Select an update to view details.</div>}</div>
            </div>
        )}
        {viewMode === 'report' && <ReportView updates={filteredUpdates} />}
        {viewMode === 'executive' && <ExecutiveReportView updates={filteredUpdates} settings={settings} summaryContent={executiveSummaryContent} onExportPdf={handleExportPdf} isGeneratingPdf={isGeneratingPdf} filters={executiveSummaryFilters} onFilterChange={onExecutiveSummaryFilterChange} onResetFilters={onResetExecutiveSummaryFilters} />}
        {viewMode === 'chart' && <ChartView updates={updates} settings={settings} />}
        {viewMode === 'goalTactic' && <GoalTacticView settings={settings} />}
    </div>
  );
}

// --- Login/Signup Component (Used by AppController) ---
function AuthScreen({ message }) {
    const { signIn, signUp, sendPasswordReset } = useAuth();
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetMessage, setResetMessage] = useState('');
    const [resetSent, setResetSent] = useState(false);

    useEffect(() => {
      if (message) {
        setModalMessage(message);
        setShowModal(true);
      }
    }, [message]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setResetSent(false);

        if (!email || !password) {
            setError('Email and password are required.');
            return;
        }

        if (!isLogin && password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        let result;
        if (isLogin) {
            result = await signIn(email, password);
        } else {
            result = await signUp(email, password);
        }

        if (!result.success) {
            setError(result.message);
        } else {
            setModalMessage(isLogin ? "You have successfully signed in." : "Account created successfully. You can now sign in.");
            setShowModal(true);
        }
    };

    const handlePasswordReset = async () => {
        if (!resetEmail) {
            setResetMessage("Please enter your email address.");
            return;
        }
        const result = await sendPasswordReset(resetEmail);
        if (result.success) {
            setResetMessage("If an account with that email exists, a password reset link has been sent.");
            setResetSent(true);
            setShowResetPasswordModal(false);
        } else {
            setResetMessage(result.message);
            setResetSent(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4 font-sans">
            <div className="bg-white dark:bg-gray-800 p-10 rounded-3xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
                <h2 className="text-4xl font-extrabold text-center text-blue-800 dark:text-blue-400 mb-10 tracking-tight">BoardSpective</h2>
                <p className="text-center text-gray-600 dark:text-gray-300 mb-8 text-lg">Your strategic plan, simplified.</p>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" required className="w-full px-5 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-lg shadow-sm placeholder-gray-500 dark:bg-gray-700 dark:text-gray-200 dark:placeholder-gray-400" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className="w-full px-5 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-lg shadow-sm placeholder-gray-500 dark:bg-gray-700 dark:text-gray-200 dark:placeholder-gray-400" />
                    {!isLogin && (
                        <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm Password" required className="w-full px-5 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-lg shadow-sm placeholder-gray-500 dark:bg-gray-700 dark:text-gray-200 dark:placeholder-gray-400" />
                    )}
                    {error && <p className="text-red-500 text-sm text-center font-medium mt-3">{error}</p>}
                    {message && <p className="text-red-500 text-sm text-center font-medium mt-3">{message}</p>}
                    {resetSent && <p className="text-green-500 text-sm text-center font-medium mt-3">Password reset email sent. Please check your inbox.</p>}
                    <button type="submit" className="w-full py-3.5 px-4 text-white font-bold bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg transition-all duration-300 ease-in-out text-xl transform hover:scale-[1.01]">
                        {isLogin ? 'Sign In' : 'Sign Up'}
                    </button>
                </form>
                <div className="mt-8 text-center">
                    <button onClick={() => {setIsLogin(!isLogin); setError(''); setResetSent(false);}} className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 text-sm">
                        {isLogin ? 'Need an account? Sign Up' : 'Already have an account? Sign In'}
                    </button>
                    <p className="font-semibold text-gray-600 dark:text-gray-300 text-base mt-4">Account creation is by invitation only.</p>
                    {isLogin && (
                        <button onClick={() => {setShowResetPasswordModal(true); setResetSent(false); setResetMessage('');}} className="font-semibold text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-300 text-sm mt-2 block">Forgot Password?</button>
                    )}
                </div>
            </div>
            <Modal
                show={showModal}
                title="Authentication"
                message={modalMessage}
                onClose={() => setShowModal(false)}
            />
            <Modal
                show={showResetPasswordModal}
                title="Reset Password"
                onClose={() => { setShowResetPasswordModal(false); setResetEmail(''); setResetMessage(''); setResetSent(false); }}
                onConfirm={handlePasswordReset}
                confirmText="Send Reset Email"
                cancelText="Cancel"
            >
                <p className="text-gray-700 dark:text-gray-300 mb-4">Enter your email address to receive a password reset link.</p>
                <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Email address"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                />
                {resetMessage && <p className="text-sm mt-2 text-center text-blue-600 dark:text-blue-300">{resetMessage}</p>}
            </Modal>
        </div>
    );
}

// Function to generate the executive summary dynamically
function generateExecutiveSummary(updates, settings, filters) {
    const summaryContent = [];
    const goalsOrder = settings.goals || [];
    const goalDescriptions = settings.descriptions || {};
    const tacticDescriptions = settings.tacticDescriptions || {};

    // Apply filters to updates
    let filteredUpdates = updates.filter(update => {
        const updateDate = new Date(update.date * 1000);
        const startDate = filters.startDate;
        const endDate = filters.endDate;

        const isDateInRange = (!startDate || updateDate >= startDate) && (!endDate || updateDate <= endDate);
        const isCommitteeMatch = !filters.committee || update.committee === filters.committee;
        const isGoalMatch = !filters.goal || update.goal === filters.goal;
        const isStrategyMatch = !filters.strategy || update.strategy === filters.strategy;
        const isTacticMatch = !filters.tactic || update.tactic === filters.tactic;
        const isSchoolYearMatch = !filters.schoolYear || update.schoolYear === filters.schoolYear;
        // Removed isStatusMatch filter
        // const isStatusMatch = Array.isArray(filters.statuses) && (filters.statuses.length === 0 || filters.statuses.includes(update.status));

        const searchStr = `${update.memberName} ${update.notes} ${update.committee} ${update.goal} ${update.strategy} ${update.tactic} ${update.schoolYear} ${update.status}`.toLowerCase();
        const isSearchMatch = !filters.search || searchStr.includes(filters.search.toLowerCase());

        return isDateInRange && isCommitteeMatch && isGoalMatch && isStrategyMatch && isTacticMatch && isSchoolYearMatch && isSearchMatch; // Removed isStatusMatch
    });

    // Group updates by goal and calculate monthly counts for sparklines
    const updatesByGoal = filteredUpdates.reduce((acc, update) => {
        if (update.goal) {
            if (!acc[update.goal]) {
                acc[update.goal] = { updates: [], monthlyCounts: {} };
            }
            acc[update.goal].updates.push(update);

            const date = new Date(update.date * 1000);
            const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
            acc[update.goal].monthlyCounts[monthKey] = (acc[update.goal].monthlyCounts[monthKey] || 0) + 1;
        }
        return acc;
    }, {});

    // Sort goals based on the order defined in settings, or alphabetically if not defined
    const sortedGoals = [...new Set([...goalsOrder, ...Object.keys(updatesByGoal)])].sort((a, b) => {
        const indexA = goalsOrder.indexOf(a);
        const indexB = goalsOrder.indexOf(b);
        if (indexA === -1 && indexB === -1) return a.localeCompare(b); // Both not in order, sort alphabetically
        if (indexA === -1) return 1; // A not in order, B is, so B comes first
        if (indexB === -1) return -1; // B not in order, A is, so A comes first
        return indexA - indexB; // Both in order, sort by their defined order
    });

    sortedGoals.forEach(goal => {
        const goalData = updatesByGoal[goal];
        const monthlyCountsArray = goalData ? Object.keys(goalData.monthlyCounts)
            .sort() // Sort by month key
            .map(month => ({ month, count: goalData.monthlyCounts[month] })) : [];

        summaryContent.push({ type: 'goalTitle', text: goal, chartData: monthlyCountsArray }); // Structured object for goal title with chart data
        summaryContent.push({ type: 'goalDescription', text: goalDescriptions[goal] || 'No description available.' }); // Structured object for goal description

        const relevantUpdates = goalData ? goalData.updates : [];
        // Sort updates for consistency (e.g., by date descending)
        relevantUpdates.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));

        relevantUpdates.forEach(update => {
            const updateDate = update.date ? new Date(update.date * 1000).toLocaleDateString() : 'N/A';
            summaryContent.push({
                type: 'updateEntry',
                data: {
                    committee: update.committee,
                    date: updateDate,
                    notes: update.notes,
                    memberName: update.memberName || 'N/A', // Now displays full name
                    strategy: update.strategy || 'N/A',
                    tactic: update.tactic || 'N/A',
                    tacticDescription: (update.tactic && tacticDescriptions[update.tactic]) ? tacticDescriptions[update.tactic] : null
                }
            });
        });
    });

    return summaryContent; // Return the structured array
}


function MainApp() {
  const [page, setPage] = useState('dashboard');
  const { currentUser, userRole, signOutUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(() => localStorage.getItem('activePlanId') || null);
  const [settings, setSettings] = useState(defaultPlanSettings);
  const [updates, setUpdates] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [executiveSummaryContent, setExecutiveSummaryContent] = useState([]);

  // States for the main app's modal
  const [mainAppShowModal, setMainAppShowModal] = useState(false);
  const [mainAppModalTitle, setMainAppModalTitle] = useState('');
  const [mainAppModalMessage, setMainAppModalMessage] = useState('');
  const [mainAppModalConfirmAction, setMainAppModalConfirmAction] = useState(null);
  const [mainAppModalConfirmText, setMainAppModalConfirmText] = useState('');
  const [mainAppModalCancelText, setMainAppModalCancelText] = useState('Close');

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);


  const [plansLoaded, setPlansLoaded] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [updatesLoaded, setUpdatesLoaded] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Executive Summary Filters
  const [executiveSummaryFilters, setExecutiveSummaryFilters] = useState({
      startDate: null,
      endDate: null,
      search: '', // Added search filter
      committee: '',
      goal: '',
      strategy: '', // Added strategy filter
      tactic: '',   // Added tactic filter
      schoolYear: '', // Added schoolYear filter
      // Removed statuses filter
  });

  const handleExecutiveSummaryFilterChange = (filterName, value) => {
      setExecutiveSummaryFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleResetExecutiveSummaryFilters = () => {
      setExecutiveSummaryFilters({
          startDate: null,
          endDate: null,
          search: '',
          committee: '',
          goal: '',
          strategy: '',
          tactic: '',
          schoolYear: '',
          // Removed statuses filter
      });
  };

  const dataLoaded = useMemo(() => {
      const baseLoaded = plansLoaded && (plans.length === 0 || (settingsLoaded && updatesLoaded)); 
      
      if (userRole === 'admin') {
          return baseLoaded && usersLoaded;
      }
      return baseLoaded;
  }, [plansLoaded, plans.length, settingsLoaded, updatesLoaded, usersLoaded, userRole]);


  // Effect 1: Fetch Plans and set activePlanId
  useEffect(() => {
      if (!currentUser) {
          setPlans([]);
          setPlansLoaded(false);
          setActivePlanId(null);
          return;
      }

      console.log("MainApp: Effect 1 - Fetching plans...");
      const unsubPlans = onSnapshot(collection(db, "plans"), (snapshot) => {
          const plansData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setPlans(plansData);
          setPlansLoaded(true);

          if (!activePlanId || !plansData.some(p => p.id === activePlanId)) {
              if (plansData.length > 0) {
                  const firstPlanId = plansData[0].id;
                  setActivePlanId(firstPlanId);
                  localStorage.setItem('activePlanId', firstPlanId);
                  console.log("MainApp: Active plan set to first available:", firstPlanId);
              } else {
                  setActivePlanId(null);
                  localStorage.removeItem('activePlanId');
                  console.log("MainApp: No plans found, activePlanId set to null.");
              }
          }
      }, (error) => {
          console.error("Error fetching plans:", error);
          setPlansLoaded(true);
          setActivePlanId(null);
      });

      return () => {
          console.log("MainApp: Effect 1 - Cleaning up plans listener.");
          unsubPlans();
      };
  }, [currentUser, activePlanId]);


  // Effect 2: Fetch Active Plan's Data (Settings, Updates)
  useEffect(() => {
      let unsubSettings, unsubUpdates;

      setSettingsLoaded(false);
      setUpdatesLoaded(false);
      setExecutiveSummaryContent([]);

      if (currentUser && activePlanId) {
          console.log(`MainApp: Effect 2 - Fetching data for activePlanId: ${activePlanId}`);

          unsubSettings = onSnapshot(doc(db, "plans", activePlanId, "settings", "plan_settings"), (docSnap) => {
              if(docSnap.exists()) {
                  setSettings({ ...defaultPlanSettings, ...docSnap.data() });
              } else {
                  console.log("No settings document found for plan, initializing with defaults.");
                  setDoc(doc(db, "plans", activePlanId, "settings", "plan_settings"), defaultPlanSettings)
                      .then(() => setSettings(defaultPlanSettings))
                      .catch(e => console.error("Error initializing settings:", e));
              }
              setSettingsLoaded(true);
          }, (error) => {
              console.error("Error fetching settings:", error);
              setSettingsLoaded(true);
          });

          unsubUpdates = onSnapshot(collection(db, "plans", activePlanId, "updates"), (snapshot) => {
              const updatesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              setUpdates(updatesData);
              setUpdatesLoaded(true);
          }, (error) => {
              console.error("Error fetching updates:", error);
              setUpdatesLoaded(true);
          });

          return () => {
              console.log("MainApp: Effect 2 - Cleaning up active plan data listeners.");
              if(unsubSettings) unsubSettings();
              if(unsubUpdates) unsubUpdates();
          };
      } else if (currentUser && !activePlanId) {
          setSettings(defaultPlanSettings);
          setUpdates([]);
          setExecutiveSummaryContent([]);
          setSettingsLoaded(true);
          setUpdatesLoaded(true);
          console.log("MainApp: No active plan, dependent data states reset and marked loaded.");
      }
  }, [currentUser, activePlanId]);


  // Effect 3: Fetch Users (Admin Only)
  useEffect(() => {
      if (!currentUser) {
          setUsers([]);
          setUsersLoaded(false);
          return;
      }

      if (userRole === 'admin') {
          console.log("MainApp: Effect 3 - Fetching users (admin role detected)...");
          const unsubUsers = onSnapshot(collection(db, "users"), (snapshot) => {
              const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              setUsers(usersData);
              setUsersLoaded(true);
          }, (error) => {
              console.error("Error fetching users:", error);
              setUsersLoaded(true);
          });
          return () => {
              console.log("MainApp: Effect 3 - Cleaning up users listener.");
              unsubUsers();
          };
      } else {
          console.log("MainApp: Effect 3 - Not admin, users data not fetched. Setting usersLoaded to true.");
          setUsers([]);
          setUsersLoaded(true);
      }
  }, [currentUser, userRole]);

  // Effect 4: Generate Executive Summary whenever updates or settings change, or filters change
  useEffect(() => {
    if (updatesLoaded && settingsLoaded) {
        const generatedSummary = generateExecutiveSummary(updates, settings, executiveSummaryFilters);
        setExecutiveSummaryContent(generatedSummary);
    }
  }, [updates, settings, updatesLoaded, settingsLoaded, executiveSummaryFilters]);


  const handleSaveSettings = async (newSettings, newUsers) => {
      if (!activePlanId) {
          console.error("No active plan selected to save settings.");
          setMainAppModalTitle("Save Error");
          setMainAppModalMessage("Failed to save settings: No strategic plan selected. Please create or select one first.");
          setMainAppModalConfirmAction(null);
          setMainAppModalConfirmText(null);
          setMainAppModalCancelText("Close");
          setMainAppShowModal(true);
          return;
      }
      const batch = writeBatch(db);
      const planSettingsDocRef = doc(db, "plans", activePlanId, "settings", "plan_settings");
      batch.set(planSettingsDocRef, newSettings, { merge: true });

      newUsers.forEach(user => {
          const userDocRef = doc(db, "users", user.id);
          const updateData = {};
          if (user.firstName !== undefined) updateData.firstName = user.firstName;
          if (user.lastName !== undefined) updateData.lastName = user.lastName;
          if (user.role !== undefined) updateData.role = user.role;
          
          if (Object.keys(updateData).length > 0) {
              batch.update(userDocRef, updateData);
          }
      });
      
      try {
          await batch.commit();
          console.log("Settings and user roles saved successfully!");
          setPage('dashboard');
      } catch (error) {
          console.error("Error saving settings and user roles:", error);
          setMainAppModalTitle("Save Error");
          setMainAppModalMessage("Failed to save settings. Check console for details.");
          setMainAppModalConfirmAction(null);
          setMainAppModalConfirmText(null);
          setMainAppModalCancelText("Close");
          setMainAppShowModal(true);
      }
  };

  const handleSaveUpdate = async (update) => {
      if (!activePlanId) {
          setMainAppModalTitle("Save Error");
          setMainAppModalMessage("Please create or select a strategic plan first in Settings.");
          setMainAppModalConfirmAction(null);
          setMainAppModalConfirmText(null);
          setMainAppModalCancelText("Close");
          setMainAppShowModal(true);
          return;
      }
      const dateAsDateObject = update.date instanceof Date ? update.date : new Date(update.date);
      
      const updateData = { 
          // Ensure all required fields are present, even if empty strings
          memberName: update.memberName || '',
          committee: update.committee || '',
          goal: update.goal || '',
          strategy: update.strategy || '',
          tactic: update.tactic || '',
          schoolYear: update.schoolYear || '',
          notes: update.notes || '',
          status: update.status || '',
          attachments: update.attachments || [],
          date: dateAsDateObject.getTime() / 1000
      };
      
      if (update.id) {
          const updateRef = doc(db, "plans", activePlanId, "updates", update.id); 
          try {
              await updateDoc(updateRef, updateData);
              console.log("Update edited successfully!");
          } catch (error) {
              console.error("Error editing document: ", error);
              setMainAppModalTitle("Save Error");
              setMainAppModalMessage("Failed to edit update. Check console for details.");
              setMainAppModalConfirmAction(null);
              setMainAppModalConfirmText(null);
              setMainAppModalCancelText("Close");
              setMainAppShowModal(true);
          }
      } else {
          delete updateData.id; 
          try {
              await addDoc(collection(db, "plans", activePlanId, "updates"), updateData); 
              console.log("New update added successfully!");
          } catch (error) {
              console.error("Error adding document: ", error);
              setMainAppModalTitle("Save Error");
              setMainAppModalMessage("Failed to add update. Check console for details.");
              setMainAppModalConfirmAction(null);
              setMainAppModalConfirmText(null);
              setMainAppModalCancelText("Close");
              setMainAppShowModal(true);
          }
      }
      setEditingUpdate(null);
      setPage('dashboard');
  };

  const handleDelete = (id) => {
      if (!activePlanId) {
          setMainAppModalTitle("Delete Error");
          setMainAppModalMessage("No active plan selected.");
          setMainAppModalConfirmAction(null);
          setMainAppModalConfirmText(null);
          setMainAppModalCancelText("Close");
          setMainAppShowModal(true);
          return;
      }
      setMainAppModalTitle("Confirm Deletion");
      setMainAppModalMessage('Are you sure you want to delete this update? This action cannot be undone.');
      setMainAppModalConfirmText("Yes, Delete");
      setMainAppModalCancelText("Cancel");
      setMainAppModalConfirmAction(() => async () => {
          try {
              await deleteDoc(doc(db, "plans", activePlanId, "updates", id)); 
              console.log("Update deleted successfully!");
              if (editingUpdate?.id === id) {
                  setEditingUpdate(null);
              }
          } catch (error) {
              console.error("Error deleting document: ", error);
              setMainAppModalTitle("Delete Error");
              setMainAppModalMessage("Failed to delete update. Check console for details.");
              setMainAppModalConfirmAction(null);
              setMainAppModalConfirmText(null);
              setMainAppModalCancelText("Close");
              setMainAppShowModal(true);
          } finally {
              setMainAppShowModal(false);
              setMainAppModalConfirmAction(null);
          }
      });
      setMainAppShowModal(true);
  };

  const handleCreatePlan = async (planName) => {
      if(!planName) {
          setMainAppModalTitle("Create Plan Error");
          setMainAppModalMessage("Plan name cannot be empty.");
          setMainAppModalConfirmAction(null);
          setMainAppModalConfirmText(null);
          setMainAppModalCancelText("Close");
          setMainAppShowModal(true);
          return;
      }
      try {
          const newPlanRef = await addDoc(collection(db, "plans"), {
              name: planName,
              createdAt: new Date(),
              executiveSummary: ""
          });
          await setDoc(doc(db, "plans", newPlanRef.id, "settings", "plan_settings"), defaultPlanSettings);
          setActivePlanId(newPlanRef.id);
          localStorage.setItem('activePlanId', newPlanRef.id);
          console.log("New plan created:", planName);
      } catch (error) {
          console.error("Error creating new plan:", error);
          setMainAppModalTitle("Create Plan Error");
          setMainAppModalMessage("Failed to create new plan. Check console for details.");
          setMainAppModalConfirmAction(null);
          setMainAppModalConfirmText(null);
          setMainAppModalCancelText("Close");
          setMainAppShowModal(true);
      }
  };

  const handleExportPdf = async (elementId, filename) => {
      setIsGeneratingPdf(true);
      if (typeof window.html2canvas === 'undefined' || typeof window.jspdf === 'undefined') {
          try {
              await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
              await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
          } catch (error) {
              console.error("Failed to load PDF generation libraries:", error);
              setMainAppModalTitle("PDF Export Error");
              setMainAppModalMessage("Failed to load PDF generation libraries. Please try again.");
              setMainAppModalConfirmAction(null);
              setMainAppModalConfirmText(null);
              setMainAppModalCancelText("Close");
              setMainAppShowModal(true);
              setIsGeneratingPdf(false);
              return;
          }
      }

      try {
          const input = document.getElementById(elementId);
          const canvas = await window.html2canvas(input, {
              scale: 2,
              useCORS: true,
              logging: false,
          });
          const imgData = canvas.toDataURL('image/png');
          const pdf = new window.jspdf.jsPDF('p', 'mm', 'a4');
          const imgWidth = 210;
          const pageHeight = 297;
          const imgHeight = canvas.height * imgWidth / canvas.width;
          let heightLeft = imgHeight;
          let position = 0;
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
          while (heightLeft >= 0) {
              position = heightLeft - imgHeight;
              pdf.addPage();
              pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
              heightLeft -= pageHeight;
          }
          pdf.save(filename);
      } catch (error) {
          console.error("Error generating PDF:", error);
          setMainAppModalTitle("PDF Export Error");
          setMainAppModalMessage("Failed to generate PDF. Please try again.");
          setMainAppModalConfirmAction(null);
          setMainAppModalConfirmText(null);
          setMainAppModalCancelText("Close");
          setMainAppShowModal(true);
      } finally {
          setIsGeneratingPdf(false);
      }
  };

  const loadScript = (src) => {
      return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
      });
  };

  const renderPage = () => {
    if (!dataLoaded) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-120px)] bg-gray-50 dark:bg-gray-900 p-6 rounded-lg shadow-inner">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-lg text-gray-700 dark:text-gray-300">Loading strategic plan data...</p>
                </div>
            </div>
        );
    }

    switch (page) {
      case 'form':
        return <UpdateEntryForm onSave={handleSaveUpdate} onCancel={() => {setPage('dashboard'); setEditingUpdate(null);}} settings={settings} updateToEdit={editingUpdate} users={users} />;
      case 'settings':
        return <SettingsPage settings={settings} users={users} onSave={handleSaveSettings} onCancel={() => setPage('dashboard')} onCreatePlan={handleCreatePlan} />;
      case 'executive':
        return activePlanId ? (
          <ExecutiveReportView
            summaryContent={executiveSummaryContent}
            updates={updates}
            settings={settings}
            onExportPdf={handleExportPdf}
            isGeneratingPdf={isGeneratingPdf}
            filters={executiveSummaryFilters}
            onFilterChange={handleExecutiveSummaryFilterChange}
            onResetFilters={handleResetExecutiveSummaryFilters}
          />
        ) : (
          <div className="text-center p-10 bg-white dark:bg-gray-800 rounded-lg shadow dark:text-gray-200">Please create or select a strategic plan first in Settings.</div>
        );
      case 'chart':
        return activePlanId ? (
            <ChartView updates={updates} settings={settings} />
        ) : (
            <div className="text-center p-10 bg-white dark:bg-gray-800 rounded-lg shadow dark:text-gray-200">Please create or select a strategic plan first in Settings to view charts.</div>
        );
      case 'goalTactic':
        return activePlanId ? (
            <GoalTacticView settings={settings} />
        ) : (
            <div className="text-center p-10 bg-white dark:bg-gray-800 rounded-lg shadow dark:text-gray-200">Please create or select a strategic plan first in Settings to view goals and tactics.</div>
        );
      default: // 'dashboard'
        return (
            <Dashboard 
                updates={updates} 
                onEdit={(update) => {setEditingUpdate(update); setPage('form');}} 
                onDelete={handleDelete} 
                settings={settings} 
                userRole={userRole} 
                onAddNew={() => { setEditingUpdate(null); setPage('form'); }} 
                activePlanId={activePlanId}
                executiveSummaryContent={executiveSummaryContent}
                handleExportPdf={handleExportPdf}
                isGeneratingPdf={isGeneratingPdf}
                executiveSummaryFilters={executiveSummaryFilters}
                onExecutiveSummaryFilterChange={handleExecutiveSummaryFilterChange}
                onResetExecutiveSummaryFilters={handleResetExecutiveSummaryFilters}
            />
        );
    }
  };

  return (
    <>
      <Header 
        setPage={setPage} 
        currentUser={currentUser} 
        userRole={userRole} 
        plans={plans} 
        activePlanId={activePlanId} 
        onPlanChange={setActivePlanId} 
        signOutUser={signOutUser} 
      />
      <main className="p-4 md:p-8">
        {renderPage()}
      </main>
      <Modal
        show={mainAppShowModal}
        title={mainAppModalTitle}
        message={mainAppModalMessage}
        onClose={() => {
            setMainAppShowModal(false);
            setMainAppModalConfirmAction(null);
        }}
        onConfirm={mainAppModalConfirmAction}
        confirmText={mainAppModalConfirmText}
        cancelText={mainAppModalCancelText}
      />
    </>
  );
}


// --- Main Application Wrapper Component ---
function AppController() {
    const { currentUser, loadingAuth } = useAuth();

    if(loadingAuth) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-lg text-gray-700 dark:text-gray-300">Loading BoardSpective...</p>
                </div>
            </div>
        );
    }
    if (currentUser && currentUser.role === 'disabled') {
      signOut(auth);
      return <AuthScreen message="Your account has been disabled. Please contact an administrator." />;
    }
    return currentUser ? (
        <MainApp />
    ) : (
        <AuthScreen />
    );
}


// --- Header Component (Used by App) ---
function Header({ setPage, currentUser, userRole, plans, activePlanId, onPlanChange, signOutUser }) {
  const { theme, toggleTheme } = useTheme();

  return (
    <header className="bg-white dark:bg-gray-900 shadow-md print:hidden">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <h1 className="text-2xl md:text-3xl font-bold text-blue-800 dark:text-blue-400">BoardSpective</h1>
        <div className="flex items-center space-x-4">
          {userRole === 'admin' && (
            <div className="flex items-center space-x-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 dark:text-gray-400"><path d="M21 12a9 9 0 0 1-9 9A9.75 9.75 0 0 1 6.29 18.54L4 20l1.5-4.5A9.75 9.75 0 0 1 3 12a9 9 0 0 1 9-9c6.63 0 9 5.37 9 9Z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><line x1="10" y1="9" x2="8" y2="9"></line></svg>
              <select value={activePlanId || ''} onChange={(e) => onPlanChange(e.target.value)} className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-800 dark:text-gray-200">
                {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                {!activePlanId && <option value="" disabled>No Plan Selected</option>}
              </select>
            </div>
          )}
          <div className="text-sm text-right">
            <p className="font-semibold text-gray-800 dark:text-gray-200">{currentUser?.email}</p>
            <p className="text-gray-500 dark:text-gray-400 capitalize flex items-center justify-end">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                {userRole}
            </p>
          </div>
          <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
              title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
          >
              {theme === 'light' ? (
                  <Sun size={20}/>
              ) : (
                  <Moon size={20}/>
              )}
          </button>

          {userRole === 'admin' && (
            <button onClick={() => setPage('settings')} className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-300">
              <Settings size={20}/>
              <span className="hidden md:inline">Settings</span>
            </button>
          )}
           <button onClick={() => setPage('dashboard')} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>
              <span className="hidden md:inline">Dashboard</span>
            </button>
            <button 
                onClick={signOutUser}
                className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-300"
            >
                <LogOut size={20}/>
                <span className="hidden md:inline">Logout</span>
            </button>
        </div>
      </div>
    </header>
  );
}

// --- ThemeContext and ThemeProvider ---
const ThemeContext = createContext();

const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.add(savedTheme === 'dark' ? 'dark' : 'light');
    } else {
      document.documentElement.classList.add('light');
    }
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      document.documentElement.classList.remove(prevTheme);
      document.documentElement.classList.add(newTheme);
      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// --- Main App Component (Default Export) ---
function Root() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <div className="bg-gray-100 dark:bg-gray-900 min-h-screen flex flex-col font-inter text-gray-800 dark:text-gray-200" id="main-content">
          <style>
            {`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
                body {
                    font-family: 'Inter', sans-serif;
                }
                .print-area {
                    /* Styles specific to print area if needed, e.g., for page breaks */
                }
                @media print {
                    body {
                        margin: 1cm !important; /* Set 1cm margin for all sides */
                        color: black !important; /* Ensure text is black for printing */
                        overflow: visible !important; /* Allow content to flow naturally, no hidden overflow */
                    }
                    body * {
                        visibility: hidden;
                    }
                    .print-area, .print-area * {
                        visibility: visible;
                        position: static;
                        width: auto;
                        margin: 0;
                        padding: 0; /* Reset internal padding to rely on body margin */
                        box-sizing: border-box;
                    }
                    /* Specific styles for text within print-area to prevent overflow */
                    .print-area p, .print-area h1, .print-area h3, .print-area div {
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                        max-width: 100%; /* Ensure text and divs don't overflow their container */
                    }
                    /* Ensure elements within updateEntry don't cause overflow */
                    .print-area .border-l-4 { /* This targets the updateEntry div */
                        padding-left: 0.5rem; /* Adjust as needed for print */
                        border-left: 4px solid #bfdbfe; /* Re-apply border color for print */
                    }
                }
            `}
        </style>
        <AppController />
      </div>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default Root;
